// PKGV.D4 (#804) — Tenant-level exec summary. Recharts, not <PKGCanvas />:
// the CIO audience consumes metrics, not a graph (see issue #804 "Why
// Recharts, not React Flow").
//
// Honest-metric-states discipline (S1, Sprint-D4-adjudication.md): every
// empty/gated/cut metric is labelled for WHY it's empty, never shown as a
// real signal. Three distinct empty classes here:
//   - Decision/Risk density bars: grey "not yet linked" — TRANSIENT, clears
//     automatically once real pkg_edges exist for that type (self-healing,
//     data-driven `linked` flag — see exec-summary.service.ts).
//   - Token tile: two independent states (enrichment off vs on-zero-usage) —
//     an admin must be able to tell which.
//   - Orphan breakdown: CURRENT-STATE ONLY, no time axis (Addendum 2) — the
//     originally-specced 90-day trend was cut because no historical data
//     exists anywhere; do not add a date dimension here.

import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from 'recharts';
import { apiFetch } from '../../lib/api';
import { useMyTenant } from '../../hooks/useMyTenant';

type DensityEntityType = 'decision' | 'risk' | 'requirement' | 'user_story' | 'artefact';
type OrphanType = 'REQ_NO_ADR' | 'RISK_NO_MITIGATION' | 'MEETING_NO_DECISIONS';

interface EdgeDensityRow {
  entityType: DensityEntityType;
  totalEntities: number;
  linked: boolean;
  totalEdges?: number;
  avgEdgesPerEntity?: number;
  orphanCount?: number;
}

interface TenantPKGSummary {
  edgeDensity: EdgeDensityRow[];
  orphanBreakdown: Record<OrphanType, number>;
  governance: { adrAddressingRate: number; riskMitigationRate: number };
  enrichmentUsage: { enrichmentEnabled: boolean; totalTokens: number; eventCount: number };
  hasProjects: boolean;
}

const DENSITY_LABELS: Record<DensityEntityType, string> = {
  decision: 'Decision', risk: 'Risk', requirement: 'Requirement',
  user_story: 'Story', artefact: 'Artefact',
};

const ORPHAN_LABELS: Record<OrphanType, string> = {
  REQ_NO_ADR: 'Requirements not mentioned in an ADR',
  RISK_NO_MITIGATION: 'Risks with no mitigation plan',
  MEETING_NO_DECISIONS: 'Meetings with no decisions captured',
};

const COLOR_VIOLET = '#7c3aed';
const COLOR_AMBER = '#d97706';
const COLOR_RED = '#dc2626';
const COLOR_GREY = '#9ca3af';

function densityColor(row: EdgeDensityRow): string {
  if (!row.linked) return COLOR_GREY;
  const avg = row.avgEdgesPerEntity ?? 0;
  if (avg >= 3) return COLOR_VIOLET;
  if (avg >= 1) return COLOR_AMBER;
  return COLOR_RED;
}

// `rate: null` means no denominator (no ADRs authored / no risks
// recorded yet) — structurally distinct from a real 0%. R1 finding
// (Sprint-D4-adversarial-review.md): rendering both as the same solid
// red "0%" tells a CIO "governance is failing" when the honest read is
// "nothing to measure yet." Never collapse these two states.
function Gauge({ label, rate, healthyAt, noDataLabel }: { label: string; rate: number | null; healthyAt: number; noDataLabel: string }) {
  if (rate === null) {
    return (
      <div style={{ textAlign: 'center', flex: 1, minWidth: 180 }}>
        <ResponsiveContainer width="100%" height={140}>
          <PieChart>
            <Pie data={[{ value: 1 }]} dataKey="value" startAngle={180} endAngle={0} innerRadius="65%" outerRadius="100%" cy="90%">
              <Cell fill="#e5e7eb" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ marginTop: -60, fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
          {noDataLabel}
        </div>
        <div style={{ marginTop: 44, fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
          {label}
        </div>
      </div>
    );
  }

  const pct = Math.round(rate * 100);
  const color = rate >= healthyAt ? COLOR_VIOLET : rate >= healthyAt * 0.6 ? COLOR_AMBER : COLOR_RED;
  const data = [{ value: rate }, { value: 1 - rate }];
  return (
    <div style={{ textAlign: 'center', flex: 1, minWidth: 180 }}>
      <ResponsiveContainer width="100%" height={140}>
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            startAngle={180}
            endAngle={0}
            innerRadius="65%"
            outerRadius="100%"
            cy="90%"
          >
            <Cell fill={color} />
            <Cell fill="#e5e7eb" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <div style={{ marginTop: -60, fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--text)' }}>
        {pct}%
      </div>
      <div style={{ marginTop: 44, fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
        {label}
      </div>
    </div>
  );
}

export default function ExecPKGSummary() {
  const { myTenant } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const [summary, setSummary] = useState<TenantPKGSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;
    apiFetch<TenantPKGSummary>(`/tenants/${tenantId}/pkg/summary`)
      .then(setSummary)
      .catch(err => setError(err.message ?? 'Failed to load knowledge graph summary.'));
  }, [tenantId]);

  if (error) {
    return <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontSize: 13 }}>{error}</p>;
  }
  if (!summary) {
    return <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontSize: 13 }}>Loading…</p>;
  }
  if (!summary.hasProjects) {
    return (
      <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontSize: 13 }}>
        No projects yet. Create a project to start building your knowledge graph.
      </p>
    );
  }

  const allEmpty = summary.edgeDensity.every(r => r.totalEntities === 0);
  if (allEmpty) {
    return (
      <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontSize: 13 }}>
        Your projects don&apos;t have any decisions, risks, or requirements yet. Visit a project&apos;s Knowledge Graph tab to see how the graph builds.
      </p>
    );
  }

  const { enrichmentUsage } = summary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
      {/* Visualization 1 — edge density heatmap */}
      <div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Edge density by entity type
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={summary.edgeDensity}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="entityType" tickFormatter={(t: DensityEntityType) => DENSITY_LABELS[t]} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip
              formatter={(_value: unknown, _name: unknown, tooltipProps: { payload?: EdgeDensityRow }) => {
                const row = tooltipProps.payload;
                if (!row) return ['', ''];
                if (!row.linked) {
                  return [`${row.totalEntities} entities — data not yet linked`, DENSITY_LABELS[row.entityType]];
                }
                const orphanPart = row.orphanCount !== undefined ? `, ${row.orphanCount} orphaned` : '';
                return [
                  `${row.totalEntities} entities, ${row.totalEdges} edges${orphanPart}`,
                  DENSITY_LABELS[row.entityType],
                ];
              }}
            />
            <Bar dataKey={(row: EdgeDensityRow) => (row.linked ? row.avgEdgesPerEntity : row.totalEntities > 0 ? 0.1 : 0)}>
              {summary.edgeDensity.map(row => (
                <Cell key={row.entityType} fill={densityColor(row)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          Grey bars mean this entity type&apos;s connections aren&apos;t linked into the knowledge graph yet — not a governance signal, a data-linking gap that will resolve on its own once fixed.
        </p>
      </div>

      {/* Visualization 2 — orphan breakdown (current-state, no time axis — Addendum 2) */}
      <div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Governance gaps right now
        </h3>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart
            layout="vertical"
            data={(Object.keys(summary.orphanBreakdown) as OrphanType[]).map(type => ({
              type, count: summary.orphanBreakdown[type],
            }))}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="type" tickFormatter={(t: OrphanType) => ORPHAN_LABELS[t]} width={220} tick={{ fontSize: 11 }} />
            <Tooltip formatter={(value: number) => [`${value}`, 'Count']} />
            <Bar dataKey="count" fill={COLOR_AMBER} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Visualization 3 — governance overlay (2 gauges; implementation-rate gauge cut, Q3) */}
      <div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Governance health
        </h3>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <Gauge
            label="ADRs mentioning a requirement"
            rate={summary.governance.adrAddressingRate}
            healthyAt={0.8}
            noDataLabel="No ADRs authored yet"
          />
          <Gauge
            label="Risks with a mitigation plan"
            rate={summary.governance.riskMitigationRate}
            healthyAt={0.6}
            noDataLabel="No risks recorded yet"
          />
        </div>
      </div>

      {/* Token-usage tile — two independent empty states (Q8) */}
      <div>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
          Enrichment token usage (last 90 days)
        </h3>
        {!enrichmentUsage.enrichmentEnabled ? (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
            Enrichment tracking is not enabled for this workspace.
          </p>
        ) : enrichmentUsage.eventCount === 0 ? (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
            No enrichment usage recorded yet.
          </p>
        ) : (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)' }}>
            <strong>{enrichmentUsage.totalTokens.toLocaleString()}</strong> tokens across{' '}
            <strong>{enrichmentUsage.eventCount.toLocaleString()}</strong> enrichment events.
          </p>
        )}
      </div>
    </div>
  );
}
